import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../auth';

interface Tenant {
  tenantId: string;
  name: string;
  enabled: number;
  status: string;
  subscriptionTier: string;
  licenseQuota: number;
  licensesUsed: number;
  nodeLicenseCount: number;
  nodeCount: number;
  ownerUserId: string;
  createdAtMs: number;
  updatedAtMs: number;
}

interface TenantListResponse {
  tenants: Tenant[];
  count: number;
  backend: string;
}

type ViewState =
  | 'loading'
  | { data: TenantListResponse }
  | { error: string };

export function Tenants() {
  const [state, setState] = useState<ViewState>('loading');

  const load = () => {
    setState('loading');
    fetchWithAuth('/api/tenants', { headers: { 'accept': 'application/json' } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<TenantListResponse>;
      })
      .then((j) => setState({ data: j }))
      .catch((e: Error) => setState({ error: e.message }));
  };

  useEffect(load, []);

  return (
    <section className="view tenants">
      <div className="view-header-row">
        <h2>Tenants</h2>
        <button onClick={load} className="refresh-btn" title="Reload">↻</button>
      </div>
      <p className="muted">
        Multi-tenant registry. Every child-table query in KF filters on
        tenant. Consumes GET /api/tenants (KF backend + stub or real
        SQLite).
      </p>

      {state === 'loading' && <p>Loading…</p>}
      {typeof state === 'object' && 'error' in state && (
        <p className="err">Tenants unreachable: {state.error}</p>
      )}
      {typeof state === 'object' && 'data' in state && state.data.count === 0 && (
        <p className="muted">No tenants seeded. Backend: <code>{state.data.backend}</code>.</p>
      )}
      {typeof state === 'object' && 'data' in state && state.data.count > 0 && (
        <>
          <p className="muted">
            {state.data.count} tenant{state.data.count === 1 ? '' : 's'}
            {' · '}
            backend <code>{state.data.backend}</code>
          </p>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Tier</th>
                <th>Licenses</th>
                <th>Nodes</th>
                <th>Tenant ID</th>
              </tr>
            </thead>
            <tbody>
              {state.data.tenants.map((t, i) => (
                <tr key={i}>
                  <td>{t.name}{t.enabled === 0 && <span className="muted"> (disabled)</span>}</td>
                  <td className={t.status === 'active' ? 'ok' : 'muted'}>{t.status}</td>
                  <td><code>{t.subscriptionTier}</code></td>
                  <td>{t.licensesUsed} / {t.licenseQuota}</td>
                  <td>{t.nodeCount}</td>
                  <td className="muted mono">{t.tenantId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
